'use client'

import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'
import type { StateStorage } from 'zustand/middleware'

import type { EventRegistrationCandidateStatus } from '@/lib/event/registration-options'
import { EVENT_REGISTRATION_CANDIDATE_STATUSES } from '@/lib/event/registration-options'
import {
  composeExpectedSalaryRange,
  parseExpectedSalaryRange,
  sanitizeNumericInput,
} from './salary-range'

// ── TTL ──────────────────────────────────────────────────
export const TALENT_POOL_SUBMISSION_TTL_MS = 3 * 24 * 60 * 60 * 1000 // 3 days

// ── Form draft shape ─────────────────────────────────────
export type TalentPoolFormDraft = {
  fullName: string
  emailAddress: string
  phoneNumber: string
  birthDate: string
  targetedRoles: string[]
  expectedSalaryMin: string
  expectedSalaryMax: string
  expectedSalaryRange: string
  currentCity: string
  willingToRelocate: 'Yes' | 'No' | ''
  status: EventRegistrationCandidateStatus | ''
  remoteOnly: boolean
}

export const emptyDraft: TalentPoolFormDraft = {
  fullName: '',
  emailAddress: '',
  phoneNumber: '',
  birthDate: '',
  targetedRoles: [],
  expectedSalaryMin: '',
  expectedSalaryMax: '',
  expectedSalaryRange: '',
  currentCity: '',
  willingToRelocate: '',
  status: '',
  remoteOnly: false,
}

const normalizeDraft = (
  draft: Partial<TalentPoolFormDraft> | null | undefined,
): TalentPoolFormDraft => {
  const candidate = draft ?? {}
  const legacyRange =
    typeof candidate.expectedSalaryRange === 'string'
      ? parseExpectedSalaryRange(candidate.expectedSalaryRange)
      : { min: '', max: '' }
  const expectedSalaryMin =
    typeof candidate.expectedSalaryMin === 'string'
      ? sanitizeNumericInput(candidate.expectedSalaryMin)
      : legacyRange.min
  const expectedSalaryMax =
    typeof candidate.expectedSalaryMax === 'string'
      ? sanitizeNumericInput(candidate.expectedSalaryMax)
      : legacyRange.max
  const status =
    typeof candidate.status === 'string' &&
    EVENT_REGISTRATION_CANDIDATE_STATUSES.includes(
      candidate.status as EventRegistrationCandidateStatus,
    )
      ? candidate.status
      : ''

  return {
    fullName: typeof candidate.fullName === 'string' ? candidate.fullName : '',
    emailAddress:
      typeof candidate.emailAddress === 'string' ? candidate.emailAddress : '',
    phoneNumber:
      typeof candidate.phoneNumber === 'string' ? candidate.phoneNumber : '',
    birthDate: typeof candidate.birthDate === 'string' ? candidate.birthDate : '',
    targetedRoles: Array.isArray(candidate.targetedRoles)
      ? Array.from(
          new Set(
            candidate.targetedRoles
              .map((role) => (typeof role === 'string' ? role.trim() : ''))
              .filter(Boolean),
          ),
        ).slice(0, 3)
      : [],
    expectedSalaryMin,
    expectedSalaryMax,
    expectedSalaryRange:
      composeExpectedSalaryRange(expectedSalaryMin, expectedSalaryMax) ||
      (typeof candidate.expectedSalaryRange === 'string'
        ? candidate.expectedSalaryRange
        : ''),
    currentCity: typeof candidate.currentCity === 'string' ? candidate.currentCity : '',
    willingToRelocate:
      candidate.willingToRelocate === 'Yes' || candidate.willingToRelocate === 'No'
        ? candidate.willingToRelocate
        : '',
    status,
    remoteOnly: candidate.remoteOnly === true,
  }
}

const normalizeSubmissionMeta = (
  state: Partial<TalentPoolStoreState>,
) => ({
  submittedAt:
    typeof state.submittedAt === 'number' && Number.isFinite(state.submittedAt)
      ? state.submittedAt
      : null,
  submittedCvId:
    typeof state.submittedCvId === 'string' && state.submittedCvId.length > 0
      ? state.submittedCvId
      : null,
})

// ── State ────────────────────────────────────────────────
type TalentPoolStoreState = {
  // Form draft (survives page reloads / auth redirect)
  draft: TalentPoolFormDraft
  /** Which file name was stored in IndexedDB (for display only). */
  storedFileName: string | null

  // Submission tracking
  submittedAt: number | null
  submittedCvId: string | null

  // Hydration gate
  hasHydrated: boolean

  // Actions
  setDraft: (partial: Partial<TalentPoolFormDraft>) => void
  setStoredFileName: (name: string | null) => void
  markSubmitted: (cvId: string) => void
  isSubmissionActive: () => boolean
  pruneExpiredSubmission: () => void
  clearAll: () => void
  markHydrated: () => void
}

// ── Storage helpers (resilient localStorage wrapper) ─────
const noopStorage: StateStorage = {
  getItem: () => null,
  setItem: () => {},
  removeItem: () => {},
}

const resolveStorage = (): StateStorage => {
  if (typeof window === 'undefined') return noopStorage

  try {
    const storage = window.localStorage
    return {
      getItem: (name) => {
        try {
          const value = storage.getItem(name)
          if (value === null) return null
          try {
            JSON.parse(value)
            return value
          } catch {
            try {
              storage.removeItem(name)
            } catch {
              // noop
            }
            return null
          }
        } catch {
          return null
        }
      },
      setItem: (name, value) => {
        try {
          storage.setItem(name, value)
        } catch {
          // noop
        }
      },
      removeItem: (name) => {
        try {
          storage.removeItem(name)
        } catch {
          // noop
        }
      },
    }
  } catch {
    return noopStorage
  }
}

const isWithinTtl = (ts: number | null) => {
  if (ts === null) return false
  return Date.now() - ts < TALENT_POOL_SUBMISSION_TTL_MS
}

// ── Store ────────────────────────────────────────────────
export const useTalentPoolStore = create<TalentPoolStoreState>()(
  persist(
    (set, get) => ({
      draft: { ...emptyDraft },
      storedFileName: null,
      submittedAt: null,
      submittedCvId: null,
      hasHydrated: false,

      setDraft: (partial) =>
        set((state) => ({
          draft: normalizeDraft({ ...state.draft, ...partial }),
        })),

      setStoredFileName: (name) => set({ storedFileName: name }),

      markSubmitted: (cvId) =>
        set({
          submittedAt: Date.now(),
          submittedCvId: cvId,
          draft: { ...emptyDraft },
          storedFileName: null,
        }),

      isSubmissionActive: () => isWithinTtl(get().submittedAt),

      pruneExpiredSubmission: () => {
        const s = get()
        if (isWithinTtl(s.submittedAt)) return
        if (s.submittedAt === null && s.submittedCvId === null) return
        set({ submittedAt: null, submittedCvId: null })
      },

      clearAll: () =>
        set({
          draft: { ...emptyDraft },
          storedFileName: null,
          submittedAt: null,
          submittedCvId: null,
        }),

      markHydrated: () => set({ hasHydrated: true }),
    }),
    {
      name: 'talent-pool-storage',
      version: 2,
      storage: createJSONStorage(resolveStorage),
      partialize: (state) => ({
        draft: state.draft,
        storedFileName: state.storedFileName,
        submittedAt: state.submittedAt,
        submittedCvId: state.submittedCvId,
      }),
      migrate: (persistedState) => {
        if (!persistedState || typeof persistedState !== 'object') {
          return persistedState
        }

        const state = persistedState as Partial<TalentPoolStoreState>
        return {
          ...state,
          draft: normalizeDraft(state.draft),
          ...normalizeSubmissionMeta(state),
        }
      },
      merge: (persistedState, currentState) => {
        const persisted =
          persistedState && typeof persistedState === 'object'
            ? (persistedState as Partial<TalentPoolStoreState>)
            : {}

        return {
          ...currentState,
          ...persisted,
          draft: normalizeDraft(persisted.draft),
          ...normalizeSubmissionMeta(persisted),
        }
      },
      onRehydrateStorage: (state) => () => {
        state.pruneExpiredSubmission()
        state.markHydrated()
      },
    },
  ),
)
